import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back to HOTSLIP!");
            nav(next);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-md mx-auto px-4">
                    <div className="flex items-center gap-3 mb-6">
                        <Logo size={36} />
                        <div>
                            <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">Welcome back</p>
                            <h1 className="font-display text-3xl tracking-widest">LOG IN TO HOTSLIP</h1>
                        </div>
                    </div>
                    <Card className="pp-bg-1 border-[#242436] text-white">
                        <CardContent className="p-6">
                            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                                <div>
                                    <Label htmlFor="email" className="text-[#b7b7c6]">Email</Label>
                                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]" data-testid="auth-email-input" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password" className="text-[#b7b7c6]">Password</Label>
                                        <Link to="/forgot-password" className="text-xs text-[#22d3ee] hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
                                    </div>
                                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]" data-testid="auth-password-input" />
                                </div>
                                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#6f3fec] hover:brightness-110 text-white" data-testid="auth-submit-button">
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}
                                </Button>
                            </form>
                            <p className="mt-4 text-sm text-[#b7b7c6]">
                                No account?{" "}
                                <Link to="/signup" className="text-[#22c55e] hover:underline" data-testid="login-to-signup-link">Sign up</Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
        </div>
    );
}
